import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Receipt, ExternalLink, FileText, X, Banknote } from "lucide-react";

const STATUS_META = {
  draft:   { label: "Draft",   color: "#94A3B8", bg: "rgba(148,163,184,0.15)" },
  sent:    { label: "Sent",    color: "#38BDF8", bg: "rgba(56,189,248,0.15)" },
  paid:    { label: "Paid",    color: "#25D366", bg: "rgba(37,211,102,0.15)" },
  overdue: { label: "Overdue", color: "#F87171", bg: "rgba(248,113,113,0.15)" },
  void:    { label: "Void",    color: "#6B7280", bg: "rgba(107,114,128,0.15)" },
};

const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);

// Extract the ISO date (YYYY-MM-DD) from a payment record's timestamp fields.
const paymentDay = (p) => String(p?.paid_at || p?.received_at || p?.date || "").slice(0, 10);

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paidFrom = searchParams.get("paid_from") || "";
  const paidTo = searchParams.get("paid_to") || "";
  const paidWindow = paidFrom && paidTo ? { from: paidFrom, to: paidTo } : null;

  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const { data: allInvoices = [], isLoading } = useQuery({
    queryKey: ["invoices", status, q],
    queryFn: () => api.get("/invoices", { params: { status: status === "all" ? undefined : status, search: q || undefined } }).then((r) => r.data),
  });

  // Apply the paid-in-window filter client-side. An invoice matches when at
  // least one of its payments has a paid_at date inside [paid_from, paid_to].
  const invoices = useMemo(() => {
    if (!paidWindow) return allInvoices;
    return allInvoices.filter((inv) =>
      (inv.payments || []).some((p) => {
        const d = paymentDay(p);
        return d && d >= paidWindow.from && d <= paidWindow.to;
      })
    );
  }, [allInvoices, paidWindow]);

  const totals = useMemo(() => {
    const t = { count: invoices.length, outstanding: 0, paid: 0, overdue: 0, receivedInWindow: 0 };
    for (const inv of invoices) {
      t.outstanding += inv.balance_due || 0;
      t.paid += inv.paid_total || 0;
      if (inv.status === "overdue") t.overdue += inv.balance_due || 0;
      if (paidWindow) {
        for (const p of inv.payments || []) {
          const d = paymentDay(p);
          if (d && d >= paidWindow.from && d <= paidWindow.to) {
            t.receivedInWindow += p.amount || 0;
          }
        }
      }
    }
    return t;
  }, [invoices, paidWindow]);

  const clearPaidWindow = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("paid_from");
    next.delete("paid_to");
    setSearchParams(next);
  };

  return (
    <div>
      <PageHeader
        overline="Billing"
        title="Invoices"
        subtitle="Send, track, and reconcile invoices with PayNow QR + bank transfer."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/settings?tab=billing" className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary border border-border rounded-sm px-3 py-2 transition-colors" data-testid="invoices-billing-profile">Billing profile</Link>
            <Button className="rounded-sm bg-primary" onClick={() => navigate("/invoices/new")} data-testid="invoices-new">
              <Plus className="h-4 w-4 mr-2" /> New invoice
            </Button>
          </div>
        }
      />
      <div className="px-6 lg:px-10 pb-10 space-y-6">
        {/* Paid-in-window filter chip */}
        {paidWindow && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-sm border border-green-400/40 bg-green-500/5 px-4 py-3"
            data-testid="paid-window-filter-chip"
          >
            <Banknote className="h-4 w-4 text-green-400 shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-widest text-green-400 font-bold">Payments received</div>
              <div className="text-sm font-semibold">
                {paidWindow.from} → {paidWindow.to}
                <span className="text-muted-foreground font-normal ml-2">
                  · {invoices.length} invoice{invoices.length === 1 ? "" : "s"} · {fmt(totals.receivedInWindow)} collected
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearPaidWindow}
              className="rounded-sm border-border text-xs uppercase tracking-widest"
              data-testid="paid-window-clear"
            >
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          </div>
        )}

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TotalCard label="Invoices" value={totals.count} tone="neutral" />
          <TotalCard label="Outstanding" value={fmt(totals.outstanding)} tone="primary" />
          <TotalCard label={paidWindow ? "Received in window" : "Received"} value={fmt(paidWindow ? totals.receivedInWindow : totals.paid)} tone="green" />
          <TotalCard label="Overdue" value={fmt(totals.overdue)} tone="red" />
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by invoice #, customer name or email…"
              className="pl-9 rounded-sm bg-secondary/40"
              data-testid="invoices-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm bg-secondary/40 w-40" data-testid="invoices-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Invoice list */}
        <Card className="rounded-sm bg-card border-border overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_2fr_1fr_1fr_1fr_1fr_60px] gap-3 px-4 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <div>Invoice #</div><div>Customer</div><div>Issue date</div><div>Due date</div><div>Total</div><div>Balance</div><div>Status</div>
          </div>
          {isLoading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && invoices.length === 0 && (
            <div className="p-12 text-center">
              <Receipt className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">No invoices yet.</div>
              <Button onClick={() => navigate("/invoices/new")} className="rounded-sm mt-4 bg-primary" data-testid="invoices-empty-new">Create your first invoice</Button>
            </div>
          )}
          {invoices.map((inv) => {
            const meta = STATUS_META[inv.status] || STATUS_META.draft;
            return (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                className="grid grid-cols-1 md:grid-cols-[1.2fr_2fr_1fr_1fr_1fr_1fr_60px] gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors border-b border-border last:border-b-0"
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="font-mono text-xs text-primary">{inv.invoice_number}</div>
                <div className="text-sm truncate">{inv.bill_to?.name}<div className="text-[10px] text-muted-foreground truncate">{inv.bill_to?.email}</div></div>
                <div className="text-xs text-muted-foreground">{inv.issue_date}</div>
                <div className="text-xs text-muted-foreground">{inv.due_date || "—"}</div>
                <div className="text-sm">{fmt(inv.total, inv.currency)}</div>
                <div className={`text-sm ${inv.balance_due > 0 ? "text-primary" : "text-muted-foreground"}`}>{fmt(inv.balance_due, inv.currency)}</div>
                <div>
                  <Badge className="rounded-sm text-[10px] px-1.5 py-0" style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.color + "44" }}>{meta.label}</Badge>
                </div>
              </Link>
            );
          })}
        </Card>
      </div>
    </div>
  );
}

function TotalCard({ label, value, tone = "neutral" }) {
  const toneClass = {
    neutral: "text-foreground",
    primary: "text-primary",
    green: "text-green-400",
    red: "text-red-400",
  }[tone];
  return (
    <Card className="rounded-sm bg-card border-border p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-display text-2xl font-black mt-1 ${toneClass}`}>{value}</div>
    </Card>
  );
}
